function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = [],
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          'form',
          `${element.form.getAttribute('id')}-${uniqueKey}`,
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute(
    'aria-expanded',
    summary.parentNode.hasAttribute('open'),
  );

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open'),
    );
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  // Skip desktop mega overlay; our hover logic handles ESC with a fade
  if (summary.closest('details.mega-menu--overlay')) return;

  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*',
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === 'minus'
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        'disabled',
        parseInt(value) <= parseInt(this.input.min),
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options['hideElement'] || province_domid,
  );

  Shopify.addListener(
    this.countryEl,
    'change',
    Shopify.bind(this.countryHandler, this),
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    // Tracks pending close animations so they can't "win" after a re-open
    this._closeAnimationState = new WeakMap();

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  cancelCloseAnimation(detailsElement) {
    const state = this._closeAnimationState?.get(detailsElement);
    if (!state) return;

    if (state.animEl && state.onEnd) {
      state.animEl.removeEventListener('transitionend', state.onEnd);
    }
    if (state.timeoutId) {
      clearTimeout(state.timeoutId);
    }

    this._closeAnimationState.delete(detailsElement);
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => {
      // Let mobile facet groups behave like normal <details> accordions (no slide-out submenu behavior)
      if (summary.closest('.mobile-facets__details')) return;

      if (summary.dataset.menuDrawerBound) return;
      summary.dataset.menuDrawerBound = 'true';
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });

    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.menu-drawer__top-close)',
    ).forEach((button) => {
      if (button.dataset.menuDrawerBound) return;
      button.dataset.menuDrawerBound = 'true';
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });

    // Make the top "CLOSE" button behave like clicking the MENU summary
    this.querySelectorAll('.menu-drawer__top-close').forEach((button) => {
      if (button.dataset.menuDrawerTopBound) return;
      button.dataset.menuDrawerTopBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const mainDetails = this.mainDetailsToggle; // <details> that wraps the menu
        if (!mainDetails) return;

        const summary = mainDetails.querySelector('summary');
        if (!summary) return;

        // If the drawer is open, close it using the same path as the MENU trigger
        if (mainDetails.hasAttribute('open')) {
          // For HeaderDrawer instances, this is the overriden closeMenuDrawer()
          this.closeMenuDrawer(event, summary);
          summary.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector('summary'),
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector('button'),
      );
      summaryElement.nextElementSibling.removeEventListener(
        'transitionend',
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      // Always manage the toggle ourselves (more reliable than native default action)
      event.preventDefault();

      if (isOpen) {
        this.closeMenuDrawer(event, summaryElement);
      } else {
        this.cancelCloseAnimation(detailsElement);

        detailsElement.setAttribute('open', '');
        this.openMenuDrawer(summaryElement);
      }

      // Fix: matchMedia needs .matches (your current code always runs)
      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty(
          '--viewport-height',
          `${window.innerHeight}px`,
        );
      }

      return;
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              'transitionend',
              addTrapFocus,
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    this.cancelCloseAnimation(this.mainDetailsToggle);

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle
      .querySelectorAll('.submenu-open')
      .forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`,
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    if (!detailsElement) return;

    // If this button lives on the MAIN drawer <details>
    // (your top “CLOSE” in the mini header), we want a FULL drawer close
    if (detailsElement === this.mainDetailsToggle) {
      const summary = this.mainDetailsToggle.querySelector('summary');
      // Use the same path as clicking the backdrop / hamburger
      this.closeMenuDrawer(event, summary);
    } else {
      // For nested submenus, keep the existing “back” behavior
      this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  getMenuDrawerAnimationTarget(detailsElement) {
    // Facets drawer slides using .mobile-facets__inner
    const facetsInner = detailsElement.querySelector('.mobile-facets__inner');
    if (facetsInner) return facetsInner;

    // Main menu drawer slides using .menu-drawer (direct child of the main <details>)
    const menuDrawer = detailsElement.querySelector(':scope > .menu-drawer');
    if (menuDrawer) return menuDrawer;

    // Submenus slide using the element right after the summary (often .menu-drawer__submenu)
    const summary = detailsElement.querySelector(':scope > summary');
    if (summary && summary.nextElementSibling)
      return summary.nextElementSibling;

    return null;
  }

  getMaxTransitionTimeMs(el) {
    if (!el) return 0;
    const styles = window.getComputedStyle(el);

    const toMs = (s) => {
      const str = (s || '').trim();
      if (!str) return 0;
      if (str.endsWith('ms')) return parseFloat(str);
      if (str.endsWith('s')) return parseFloat(str) * 1000;
      const n = parseFloat(str);
      return Number.isFinite(n) ? n : 0;
    };

    const durations = styles.transitionDuration.split(',').map(toMs);
    const delays = styles.transitionDelay.split(',').map(toMs);

    const totals = durations.map(
      (d, i) => d + (delays[i] ?? delays[delays.length - 1] ?? 0),
    );
    return Math.max(0, ...totals);
  }

  closeAnimation(detailsElement) {
    // If a close is already pending for this <details>, kill it.
    // (Prevents "open -> old close finishes -> immediate close" bugs.)
    this.cancelCloseAnimation(detailsElement);

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    const animEl = this.getMenuDrawerAnimationTarget(detailsElement);

    // Token = identity for THIS close cycle. If we reopen, token changes / state deleted.
    const token = Symbol('drawer-close');

    const finish = () => {
      const state = this._closeAnimationState.get(detailsElement);

      // If we've been canceled/reopened/overridden, do nothing.
      if (!state || state.token !== token) return;

      // Close cycle complete; clear state first.
      this._closeAnimationState.delete(detailsElement);

      // Extra safety: if something re-opened and re-added menu-opening, don't force-close.
      if (detailsElement.classList.contains('menu-opening')) return;

      detailsElement.removeAttribute('open');

      const stillOpenParent = detailsElement.closest('details[open]');
      if (stillOpenParent) {
        trapFocus(stillOpenParent, detailsElement.querySelector('summary'));
      }
    };

    // Seed state immediately so finish() can be token-guarded even in early returns.
    this._closeAnimationState.set(detailsElement, {
      token,
      animEl: null,
      onEnd: null,
      timeoutId: null,
    });

    if (!animEl || reducedMotion) {
      finish();
      return;
    }

    const duration = this.getMaxTransitionTimeMs(animEl);

    // No transition? Close immediately.
    if (!duration) {
      finish();
      return;
    }

    let done = false;
    const doneOnce = () => {
      if (done) return;
      done = true;
      finish();
    };

    const onEnd = (e) => {
      if (e.target !== animEl) return;

      // Only end on the slide transition (prevents early close if other props transition)
      if (e.propertyName && e.propertyName !== 'transform') return;

      doneOnce();
    };

    animEl.addEventListener('transitionend', onEnd, { once: true });
    const timeoutId = window.setTimeout(doneOnce, duration + 50);

    // Store handles so an open can cancel them.
    this._closeAnimationState.set(detailsElement, {
      token,
      animEl,
      onEnd,
      timeoutId,
    });
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset ||
      this.closest('.header-wrapper').classList.contains(
        'header-wrapper--border-bottom',
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add('menu-open');

    // The drawer panel scrolls on its own; start it at the top each time so
    // CLOSE and the first links are in view (it kept the last position).
    const panel = this.mainDetailsToggle.querySelector('.menu-drawer');
    if (panel) panel.scrollTop = 0;

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`,
    );
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false),
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (
          event.pointerType === 'mouse' &&
          !event.target.closest('deferred-media, product-model')
        )
          this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace(
      'shopify-section-',
      '',
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html',
            );
            const sourceQty = html.querySelector(
              '.quick-order-list-container',
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`,
      ),
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));

    // iPhone/iPad Safari only lets a YouTube iframe start with sound when the
    // tap lands INSIDE the iframe — the page's own tap on the cover doesn't
    // count, so the inserted player sat paused behind a second play button.
    // Opted-in players (data-ios-tap-through) load under the cover as the
    // shopper scrolls toward them; the cover then lets taps through to
    // YouTube's play button and hides once the player reports it's playing.
    // The player is ~1.1 MB, so: never during the page's own load, and only
    // within half a screen of view (PDP videos sit ~2/3 of a screen below the
    // fold on phones — a shopper who doesn't scroll never downloads it).
    if (
      DeferredMedia.isIOS() &&
      this.querySelector('template')?.content.querySelector(
        'iframe[data-ios-tap-through]',
      )
    ) {
      const watch = () => {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.warmYouTube();
          },
          { rootMargin: '0px 0px 50% 0px' },
        );
        observer.observe(this);
      };
      if (document.readyState === 'complete') watch();
      else window.addEventListener('load', watch, { once: true });
    }
  }

  static isIOS() {
    return (
      /iP(hone|od|ad)/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  loadContent(focus = true) {
    if (this.hasAttribute('data-warm')) {
      // tapped before the warm player finished loading: show YouTube's own
      // play button rather than inserting a second player
      this.setAttribute('loaded', true);
      return;
    }
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(
          true,
        ),
      );

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(
        content.querySelector('video, model-viewer, iframe'),
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == 'VIDEO' &&
        deferredElement.getAttribute('autoplay')
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
      if (
        deferredElement.nodeName == 'IFRAME' &&
        deferredElement.hasAttribute('data-captions-off')
      ) {
        DeferredMedia.youtubeCaptionsOff(deferredElement);
      }
    }
  }

  warmYouTube() {
    if (this.getAttribute('loaded') || this.hasAttribute('data-warm')) return;
    const iframe = this.querySelector('template')
      .content.querySelector('iframe')
      .cloneNode(true);
    // cued, not autoplaying — it waits for the shopper's tap
    iframe.src = iframe.src.replace('autoplay=1', 'autoplay=0');
    this.setAttribute('data-warm', '');
    iframe.addEventListener('load', () =>
      this.setAttribute('data-warm', 'ready'),
    );
    this.appendChild(iframe);
    if (iframe.hasAttribute('data-captions-off')) {
      DeferredMedia.youtubeCaptionsOff(iframe);
    }
    DeferredMedia.watchYouTube(iframe, (state) => {
      if (state !== 1 && state !== 3) return; // playing / buffering
      this.setAttribute('loaded', true); // hides the cover (deferred-media css)
      return false;
    });
  }

  // YouTube's player only reports its state to the page after a "listening"
  // handshake, and ignores one sent before it's ready — a single handshake on
  // the iframe's load event was too early on iPhone, so state never arrived.
  // Repeat it until the player answers, like YouTube's own iframe API does.
  // onState(state, send) returning false stops listening.
  static watchYouTube(iframe, onState) {
    const send = (message) =>
      iframe.contentWindow?.postMessage(JSON.stringify(message), '*');
    let answered = false;
    const onMessage = (event) => {
      if (event.source !== iframe.contentWindow) return;
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      answered = true;
      const state =
        data.event === 'onStateChange' ? data.info : data.info?.playerState;
      if (typeof state !== 'number') return;
      if (onState(state, send) === false) {
        window.removeEventListener('message', onMessage);
      }
    };
    window.addEventListener('message', onMessage);
    // Speak up only if the player is still silent 2s after it loads. Google
    // Analytics attaches its own YouTube tracker to the same iframe; a
    // handshake of ours landing mid-way through its setup re-routes the
    // player's replies and GA's tracker throws on every state change. When
    // GA's handshake is there, the player is already talking (we read the
    // same messages) and we never send ours.
    iframe.addEventListener(
      'load',
      () =>
        setTimeout(() => {
          let tries = 0;
          const handshake = setInterval(() => {
            if (answered || !iframe.isConnected || ++tries > 120) {
              clearInterval(handshake);
              return;
            }
            send({ event: 'listening', id: 'deferred-media', channel: 'widget' });
          }, 250);
        }, 2000),
      { once: true },
    );
  }

  // YouTube turns auto-captions on for new viewers, and cc_load_policy=0
  // doesn't stop it. Once playback first starts, select "no captions track" —
  // what pressing CC off does — re-sent briefly in case captions load a beat
  // late. That choice holds when YouTube rebuffers or switches quality;
  // merely unloading the captions module didn't (the module reloaded with
  // YouTube's default and captions came back mid-video on phones). Only that
  // first start: the CC button still turns them back on afterwards.
  // Undocumented player commands; if YouTube drops them, captions show again.
  static youtubeCaptionsOff(iframe) {
    DeferredMedia.watchYouTube(iframe, (state, send) => {
      if (state !== 1) return; // 1 = playing
      [0, 1000, 3000].forEach((delay) =>
        setTimeout(() => {
          send({ event: 'command', func: 'setOption', args: ['captions', 'track', {}] });
          send({ event: 'command', func: 'unloadModule', args: ['captions'] });
        }, delay),
      );
      return false;
    });
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0,
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
      )
    ) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll('.slider-counter__link'),
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener('click', this.linkToSlide.bind(this)),
    );
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      );
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true')
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true')
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener(
        'click',
        this.autoPlayToggle.bind(this),
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      'slider-counter__link--active',
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      'aria-current',
      true,
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed,
    );
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.playSlideshow,
      );
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.pauseSlideshow,
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === 'next' && !isLastSlide) ||
      (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          swatchValue,
        );
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          'unset',
        );
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector('[data-selected-value]');
      // Strip a trailing ".0" so the size label matches the pills (e.g. "8", not
      // "8.0"). This fires immediately on change with the raw input value, before
      // product-info re-renders from the (already-stripped) server HTML, so
      // without this the label briefly flashes "8.0". Display only — the input
      // value stays raw so variant/cart matching is unaffected.
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value.replace(/\.0$/, '');
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked'),
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`,
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector('slideshow-component') &&
          this.classList.contains('complementary-products')
        ) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener(
      'storefront:signincompleted',
      this.handleStorefrontSignInCompleted.bind(this),
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement),
    );
    const quickBulkElement =
      this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          '[min]',
          event.target.dataset.min,
        ),
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          '[max]',
          event.target.max,
        ),
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          '[step]',
          event.target.step,
        ),
      );
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

// Make the SEARCH text open the same modal as the icon
document.addEventListener('click', (e) => {
  const link = e.target.closest(
    '.header__icon-group--search .header__icon-label-link',
  );
  if (!link) return;
  const group = link.closest('.header__icon-group--search');
  const trigger = group && group.querySelector('summary.header__icon--search');
  if (trigger) {
    e.preventDefault(); // don't navigate to /search
    trigger.click(); // open/close the search modal
  }
});

// Desktop MENU overlay (snippets/header-menu-overlay-tree.liquid): one
// controller for hover, click, keyboard, the backdrop and Esc.
//
// A <details> can't fade itself in: its content isn't rendered until [open],
// so there is no earlier frame to transition from. Opening therefore renders it
// with .is-entering (transparent) and drops that class a frame later; closing
// adds .is-closing, lets the fade run, then drops [open]. The header's white
// state (body.menu-overlay-open) flips at those same moments, so header and
// overlay always fade together, and coming back mid-fade simply reverses both.
(function () {
  const menus = Array.from(
    document.querySelectorAll(
      '.header .header__inline-menu details.mega-menu--overlay',
    ),
  );
  if (!menus.length) return;

  const header = document.querySelector('.section-header');
  const headerWrap = document.querySelector('.header-wrapper');
  const desktop = matchMedia('(min-width: 990px)');
  const canHover = matchMedia('(hover: hover) and (pointer: fine)');

  // Hover intent
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 90; // pointer left the panel
  const EDGE_CLOSE_DELAY = 30; // pointer left MENU for the rest of the header
  // Hovering MENU opens the menu before a click on it lands, so the first click
  // after a hover-open (pointer still on MENU, within this window) confirms it
  // instead of closing it again. Every other click toggles.
  const CLICK_AFTER_HOVER_MS = 1000;

  const parseMs = (value) => {
    const s = String(value || '').trim();
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return 220;
    return s.endsWith('ms') ? n : s.endsWith('s') ? n * 1000 : n;
  };
  const fadeMs = () =>
    parseMs(
      getComputedStyle(document.documentElement).getPropertyValue('--menu-fade'),
    );

  // per <details>: { timer, cancelFinish, openedAt, hoverOpened }
  const states = new WeakMap();
  const stateOf = (d) => {
    if (!states.has(d)) states.set(d, {});
    return states.get(d);
  };
  const summaryOf = (d) => d.querySelector(':scope > summary');
  const shellOf = (d) => d.querySelector('.mega-menu__content--overlay');
  const isShown = (d) => d.open && !d.classList.contains('is-closing');

  function setHeaderBottom() {
    const target = headerWrap || header;
    if (!target) return;
    const border = headerWrap?.classList.contains('header-wrapper--border-bottom')
      ? 1
      : 0;
    const bottom = Math.max(
      0,
      Math.round(target.getBoundingClientRect().bottom - border),
    );
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${bottom}px`,
    );
  }

  // body.menu-overlay-open turns the header white and locks page scroll. With
  // classic (non-overlay) scrollbars the lock removes the scrollbar and the
  // page would jump sideways, so pad the body by the scrollbar's width instead.
  function setHeaderState(open) {
    const body = document.body;
    if (open === body.classList.contains('menu-overlay-open')) return;
    if (open) {
      const scrollbar =
        window.innerWidth - document.documentElement.clientWidth;
      if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
      body.classList.add('menu-overlay-open');
    } else {
      body.classList.remove('menu-overlay-open');
      body.style.paddingRight = '';
    }
  }

  function open(d, { byHover = false } = {}) {
    const state = stateOf(d);
    clearTimeout(state.timer);
    if (d.classList.contains('is-closing')) {
      // Back before the fade-out finished: reverse it from where it got to.
      state.cancelFinish?.();
      state.openedAt = performance.now();
      state.hoverOpened = byHover;
      d.classList.remove('is-closing');
      setHeaderState(true);
      summaryOf(d)?.setAttribute('aria-expanded', 'true');
      return;
    }
    if (d.open) return;

    menus.forEach((m) => m !== d && close(m));
    state.openedAt = performance.now();
    state.hoverOpened = byHover;

    // Pin the sticky header and put the overlay right under it before the
    // first frame renders.
    header?.classList.add('shopify-section-header-sticky', 'menu-open');
    header?.classList.remove('shopify-section-header-hidden');
    setHeaderBottom();

    d.classList.add('is-entering');
    setHeaderState(true);
    d.open = true;
    summaryOf(d)?.setAttribute('aria-expanded', 'true');
    const shell = shellOf(d);
    if (shell) getComputedStyle(shell).opacity; // commit the transparent start
    requestAnimationFrame(() => d.classList.remove('is-entering'));
  }

  function close(d, delay = 0) {
    const state = stateOf(d);
    clearTimeout(state.timer);
    if (delay > 0) {
      state.timer = setTimeout(() => close(d), delay);
      return;
    }
    if (!isShown(d)) return;

    summaryOf(d)?.setAttribute('aria-expanded', 'false');
    setHeaderState(false);
    if (d.classList.contains('is-entering')) {
      // Closed before the fade-in started: nothing to fade out.
      finishClose(d);
      return;
    }

    d.classList.add('is-closing');
    const shell = shellOf(d);
    const done = () => {
      cleanup();
      finishClose(d);
    };
    const onEnd = (event) => {
      if (event.target === shell && event.propertyName === 'opacity') done();
    };
    const fallback = setTimeout(done, fadeMs() + 80); // if transitionend is missed
    const cleanup = () => {
      shell?.removeEventListener('transitionend', onEnd);
      clearTimeout(fallback);
      state.cancelFinish = null;
    };
    shell?.addEventListener('transitionend', onEnd);
    state.cancelFinish = cleanup;
  }

  function finishClose(d) {
    d.classList.remove('is-entering', 'is-closing');
    d.open = false;
  }

  function scheduleOpen(d) {
    const state = stateOf(d);
    clearTimeout(state.timer);
    if (d.open) {
      open(d, { byHover: true }); // already open, or fading out: keep / reverse it now
      return;
    }
    state.timer = setTimeout(() => open(d, { byHover: true }), OPEN_DELAY);
  }

  const isMouse = (event) => event.pointerType === 'mouse' && desktop.matches;

  menus.forEach((d) => {
    const summary = summaryOf(d);
    const shell = shellOf(d);
    const panel = d.querySelector('.overlay-panel');

    // Hover (mouse only: a tap on a touchscreen laptop also fires pointer
    // events, and there the click below does the work).
    summary?.addEventListener('pointerenter', (event) => {
      if (isMouse(event) && canHover.matches) scheduleOpen(d);
    });
    summary?.addEventListener('pointerleave', (event) => {
      if (!isMouse(event) || !canHover.matches) return;
      stateOf(d).hoverOpened = false; // a click after coming back is deliberate
      close(d, EDGE_CLOSE_DELAY);
    });
    panel?.addEventListener('pointerenter', (event) => {
      if (!isMouse(event)) return;
      clearTimeout(stateOf(d).timer);
      if (d.classList.contains('is-closing')) open(d);
    });
    panel?.addEventListener('pointerleave', (event) => {
      if (isMouse(event) && canHover.matches) close(d, CLOSE_DELAY);
    });

    // Click / Enter / Space on MENU. Handled here instead of the native
    // toggle, which would snap the overlay open or shut.
    summary?.addEventListener('click', (event) => {
      event.preventDefault();
      const state = stateOf(d);
      if (!isShown(d)) {
        open(d);
      } else if (
        state.hoverOpened &&
        event.detail > 0 &&
        performance.now() - state.openedAt < CLICK_AFTER_HOVER_MS
      ) {
        state.hoverOpened = false; // confirmed; the next click closes it
      } else {
        close(d);
      }
      summary.setAttribute('aria-expanded', String(isShown(d)));
    });

    // Click on the blurred backdrop (outside the white panel) closes it.
    shell?.addEventListener('click', (event) => {
      if (event.target.closest('.overlay-panel')) return;
      event.preventDefault();
      event.stopPropagation();
      close(d);
    });

    // Anything that opens or closes the <details> directly still ends up in a
    // consistent state.
    d.addEventListener('toggle', () => {
      if (d.open) {
        if (!d.classList.contains('is-closing')) setHeaderState(true);
        return;
      }
      clearTimeout(stateOf(d).timer);
      stateOf(d).cancelFinish?.();
      d.classList.remove('is-entering', 'is-closing');
      if (!menus.some((m) => m.open)) {
        setHeaderState(false);
        header?.classList.remove('menu-open');
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const d = menus.find(isShown);
    if (!d) return;
    close(d);
    summaryOf(d)?.focus({ preventScroll: true });
  });

  window.addEventListener(
    'resize',
    () => {
      if (menus.some((m) => m.open)) setHeaderBottom();
    },
    { passive: true },
  );

  window.santoMenuOverlay = { open, close };
})();

// Header: hide right-side triggers on scroll down, show on scroll up (overlay/search safe)
// PLUS: show white header background while scrolling up
(function () {
  const header = document.querySelector('.section-header .header');
  if (!header) return;

  const LOCK_CLASSES = [
    'menu-overlay-open', // desktop overlay
    'overflow-hidden-mobile', // drawers
    'overflow-hidden-tablet',
    'overflow-hidden-desktop',
  ];

  // Your markup already handles search as <details> inside .header__search
  const isSearchOpen = () =>
    !!document.querySelector('.header__search details[open]');

  // Only use body flags as locks; avoid peeking at <details[open]> directly
  const isLocked = () =>
    document.documentElement.classList.contains('overflow-hidden') ||
    document.body.classList.contains('overflow-hidden') ||
    LOCK_CLASSES.some((c) => document.body.classList.contains(c)) ||
    isSearchOpen();

  let lastY = window.pageYOffset || 0;
  const DELTA = 6; // ignore tiny jitter
  const TOP_SHOW = 24; // always show near the top

  const setHidden = (hide) =>
    header.classList.toggle('header--links-hidden', hide);
  const setReveal = (reveal) =>
    document.body.classList.toggle('header-bg-reveal', reveal);

  function onScroll() {
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;

    // Dead band (24 -> 40px) rather than one threshold: in-app browsers nudge
    // scrollY by a few px while their bars collapse during a slow scroll near
    // the top, and every flip of a <body> class is a full-page style recalc.
    if (y <= TOP_SHOW) document.body.classList.add('header-at-top');
    else if (y > TOP_SHOW + 16) document.body.classList.remove('header-at-top');

    // While locked (overlay/drawers/search) or near the very top, don't hide and don't force white
    if (isLocked() || y <= TOP_SHOW) {
      setHidden(false);
      setReveal(false);
      lastY = y;
      return;
    }

    const dy = y - lastY;
    if (Math.abs(dy) <= DELTA) return;

    if (dy > 0) {
      // Scrolling down → hide links, return to transparent
      setHidden(true);
      setReveal(false);
    } else {
      // Scrolling up → show links, paint the header white
      setHidden(false);
      setReveal(true);
    }

    lastY = y;
  }

  // When overlay/search opens, force-show links and let overlay styles take over background
  document.querySelectorAll('details.mega-menu--overlay').forEach((d) =>
    d.addEventListener('toggle', () => {
      if (d.open) setHidden(false);
    }),
  );

  document
    .querySelectorAll('.header__icons details, .header__search details')
    .forEach((d) =>
      d.addEventListener('toggle', () => {
        if (d.open) {
          setHidden(false);
          setReveal(false); // search/drawers already paint the header
        } else if (!isLocked() && (window.pageYOffset || 0) > TOP_SHOW) {
          // Closed partway down the page: the links are showing, so bring
          // back the white background they need (it was dropped on open).
          setReveal(true);
        }
      }),
    );

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();
  // Loaded (or reloaded) partway down the page: the first onScroll() has no
  // movement to react to, so the showing header would stay transparent over
  // the content. Paint it white, as a scroll-up does.
  if (!isLocked() && lastY > TOP_SHOW) setReveal(true);
})();

// Mobile "MENU" text → open/close the same drawer as the hamburger
document.addEventListener('click', (e) => {
  const menuLink = e.target.closest('.mobile-menu-link');
  if (!menuLink) return;
  e.preventDefault();

  const summary = document.querySelector(
    '#Details-menu-drawer-container > summary',
  );
  if (summary) summary.click();
});

// Keep aria-expanded in sync on the MENU link
document.addEventListener('DOMContentLoaded', () => {
  const details = document.getElementById('Details-menu-drawer-container');
  const menuLink = document.querySelector('.mobile-menu-link');
  if (!details || !menuLink) return;

  const sync = () =>
    menuLink.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  details.addEventListener('toggle', sync);
  sync();
});

// Mobile: make the MENU text open the same drawer as the hamburger
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.header__menu-trigger');
  if (!btn) return;

  e.preventDefault();
  const details = document.getElementById('Details-menu-drawer-container');
  const summary = details?.querySelector('summary.header__icon--menu');
  if (summary) summary.click(); // toggles the existing drawer
});

// A MENU tap that landed before this script loaded (captured inline in
// sections/header.liquid): open the drawer now.
window.santoMenuTriggerReady = true;
if (window.santoMenuTapPending) {
  window.santoMenuTapPending = false;
  customElements.whenDefined('header-drawer').then(() => {
    document.querySelector('.header__menu-trigger')?.removeAttribute('aria-busy');
    const details = document.getElementById('Details-menu-drawer-container');
    if (details && !details.open) details.querySelector('summary.header__icon--menu')?.click();
  });
}

// Keep aria-expanded in sync with the drawer state
document
  .getElementById('Details-menu-drawer-container')
  ?.addEventListener('toggle', (ev) => {
    const open = ev.target.open;
    document
      .querySelector('.header__menu-trigger')
      ?.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

function setAnnouncementHeightVar() {
  const el =
    document.querySelector('#shopify-section-announcement-bar') ||
    document.querySelector('.announcement-bar') ||
    document.querySelector('.announcement-bar-section') ||
    document.querySelector('[data-announcement-bar]');

  const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
  document.documentElement.style.setProperty(
    '--announcement-bar-height',
    `${h}px`,
  );
}

setAnnouncementHeightVar();
window.addEventListener('resize', setAnnouncementHeightVar);

// Extra “rock solid”: if announcement text changes size (apps, translations, wraps), track it.
(() => {
  const el =
    document.querySelector('#shopify-section-announcement-bar') ||
    document.querySelector('.announcement-bar') ||
    document.querySelector('.announcement-bar-section') ||
    document.querySelector('[data-announcement-bar]');

  if (!el || !('ResizeObserver' in window)) return;
  new ResizeObserver(setAnnouncementHeightVar).observe(el);
})();